// Sonnet 5.5 sends an answer that follows a lookup in one burst after a pause, rather than word by
// word (WEB-12341). Queuing the text and letting it out a slice per frame makes a burst read like
// a stream again, while text that already streams passes through a frame or two behind.

const TICK_MS = 16;
// A slice is this share of what is still queued, so a burst of any size drains in about a
// second (fast at first, slowing as it catches up) ...
const TICKS_TO_DRAIN = 25;
// ... and never smaller than this, so the tail doesn't crawl.
const MIN_CHARS = 6;

export type TextReveal = {
	/** Queues text to be shown. */
	push(text: string): void;
	/** Everything queued and not yet shown. */
	pending(): string;
	/** Runs `callback` once the queue has drained - at once when it is already empty. */
	afterReveal(callback: () => void): void;
	/** Drops the queue, for a round the stream abandoned. */
	clear(): void;
	/** Stops for good; queued callbacks never run. */
	cancel(): void;
};

export function createTextReveal(show: (text: string) => void): TextReveal {
	let queue = '';
	let timer: ReturnType<typeof setInterval> | null = null;
	let callbacks: Array<() => void> = [];

	const stopTimer = () => {
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
	};

	const settle = () => {
		stopTimer();
		const run = callbacks;
		callbacks = [];
		run.forEach(callback => callback());
	};

	const tick = () => {
		const size = Math.max(MIN_CHARS, Math.ceil(queue.length / TICKS_TO_DRAIN));
		const slice = queue.slice(0, size);
		queue = queue.slice(size);
		show(slice);
		if (!queue) {
			settle();
		}
	};

	return {
		push(text) {
			if (!text) {
				return;
			}
			queue += text;
			if (!timer) {
				timer = setInterval(tick, TICK_MS);
			}
		},
		pending: () => queue,
		afterReveal(callback) {
			if (queue) {
				callbacks.push(callback);
			} else {
				callback();
			}
		},
		clear() {
			queue = '';
			settle();
		},
		cancel() {
			queue = '';
			callbacks = [];
			stopTimer();
		}
	};
}
